"use strict"

import AuthService from "../auth/auth.service"
import UserService from "../user/user.service"

export default class UserConversionJourneyService {
  /**
   * @param {AuthService} AuthService
   * @param {UserService} UserService
   */
  constructor($log, AuthService, UserService) {
    "ngInject"

    this.$log = $log
    /** @type {AuthService} */
    this._authService = AuthService
    /** @type {UserService} */
    this._userService = UserService

    this._isProUser = this._authService?.authData?.isPro || false
  }

  /**
   * Checks usage limits for basic users and returns result information.
   * Pro users bypass all checks.
   *
   * @param userId {string} The unique identifier of the user
   * @param isProUser {boolean} Whether the user has pro status
   * @returns {Promise<{shouldProceed: boolean, showDialog: boolean, userUsage: object}>}
   */
  async checkUsage(userId) {
    if (this._isProUser) {
      return Promise.resolve(null)
    }

    return this._userService.getConversionJourneyByUserId(userId)
  }

  /**
   * Determines if user should be allowed to proceed and access features based on usage data
   * @param userUsage {object|null} User conversion journey data
   * @returns {boolean}
   */
  shouldProceed(userUsage) {
    return !userUsage?.blocking
  }

  /**
   * Determines if conversion dialog should be shown based on usage data
   * @param userUsage {object|null} User conversion journey data
   * @returns {boolean}
   */
  shouldShowDialog(userUsage) {
    return userUsage?.progressPercent
  }

  /**
   * Handles the usage limit flow with cached usage data
   * @param userConversionJourney {object|null} Pre-fetched user conversion journey data
   * @param user {object} User object for dialog display
   * @param userConversionJourneyDialog {function} Function to show conversion dialog
   * @param mdDialog {object} Material Dialog service instance
   * @param callback {function} Function to execute when user is allowed to proceed
   * @returns {Promise|void}
   */
  async handleUsageLimit(
    user,
    userConversionJourneyDialog,
    mdDialog,
    callback,
  ) {
    const userConversionJourney = await this.checkUsage(user.id)
    const shouldProceed = this.shouldProceed(userConversionJourney)
    const showDialog = this.shouldShowDialog(userConversionJourney)

    if (showDialog) {
      // Create the onRemoving callback that only executes if blocking is false
      const onRemoving = () => {
        if (shouldProceed) {
          callback()
        }
      }

      try {
        await userConversionJourneyDialog(
          mdDialog,
          user,
          userConversionJourney,
          onRemoving,
        )
      } catch (error) {
        // Dialog was dismissed/cancelled - this is expected when user clicks X
        // Continue execution as normal
      }
    } else if (shouldProceed) {
      callback()
    }
    // If !shouldProceed and !showDialog, do nothing (user is blocked)
  }
}
